"use client";

import { c as _c } from "react/compiler-runtime";
import { MessageCircleQuestionMark, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
type ChatMessage = {
  id: number;
  role: "user" | "assistant";
  content: string;
};
const WELCOME_MESSAGE: ChatMessage = {
  id: 0,
  role: "assistant",
  content: "Bonjour ! Je suis l'assistant IA du portail Fluidexpert. Comment puis-je vous aider ?"
};
type AiChatWindowProps = {
  open: boolean;
  onClose: () => void;
};
export function AiChatWindow(t0) {
  const $ = _c(58);
  const {
    open,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [WELCOME_MESSAGE];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [messages, setMessages] = useState(t1);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isTyping || $[3] !== messages) {
    t3 = [messages, isTyping];
    $[2] = isTyping;
    $[3] = messages;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== open) {
    t4 = () => {
      if (open && window.matchMedia("(min-width: 768px)").matches) {
        const raf = requestAnimationFrame(() => {
          requestAnimationFrame(() => textareaRef.current?.focus());
        });
        return () => cancelAnimationFrame(raf);
      }
    };
    t5 = [open];
    $[5] = open;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== open) {
    t6 = () => {
      if (!open || window.matchMedia("(min-width: 768px)").matches) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t7 = [open];
    $[8] = open;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  const [mobileStyle, setMobileStyle] = useState(undefined);
  let t8;
  let t9;
  if ($[11] !== open) {
    t8 = () => {
      const viewport = window.visualViewport;
      if (!open || !viewport || window.matchMedia("(min-width: 768px)").matches) {
        setMobileStyle(undefined);
        return;
      }
      const update = () => {
        setMobileStyle({
          height: viewport.height,
          transform: viewport.offsetTop > 0 ? `translateY(${viewport.offsetTop}px)` : undefined
        });
      };
      update();
      viewport.addEventListener("resize", update);
      viewport.addEventListener("scroll", update);
      return () => {
        viewport.removeEventListener("resize", update);
        viewport.removeEventListener("scroll", update);
        setMobileStyle(undefined);
      };
    };
    t9 = [open];
    $[11] = open;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== input || $[15] !== isTyping) {
    t10 = () => {
      const content = input.trim();
      if (!content || isTyping) {
        return;
      }
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: "user",
        content
      }]);
      setInput("");
      setIsTyping(true);
      setTimeout(() => {
        setMessages(_temp);
        setIsTyping(false);
      }, 1200);
    };
    $[14] = input;
    $[15] = isTyping;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const sendMessage = t10;
  let t11;
  if ($[17] !== sendMessage) {
    t11 = event => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    };
    $[17] = sendMessage;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleKeyDown = t11;
  const t12 = !open;
  const t13 = `fixed inset-0 z-60 md:z-30 flex flex-col overflow-hidden bg-white md:inset-auto md:bottom-6 md:right-20 md:h-[min(600px,calc(100vh-6rem))] md:w-96 md:rounded-2xl md:rounded-br-sm md:border md:border-gray-200 md:shadow-2xl md:origin-bottom-right transition-all duration-300 ease-out ${open ? "translate-y-0 opacity-100 md:translate-x-0 md:scale-100" : "pointer-events-none invisible translate-y-full opacity-0 md:translate-y-0 md:translate-x-3 md:scale-95"}`;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15"><MessageCircleQuestionMark className="h-5 w-5" /></div>;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="text-sm font-semibold leading-tight">Assistant IA</p>;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="min-w-0 flex-1">{t15}<p className="flex items-center gap-1.5 text-xs text-blue-100"><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300" />Bientôt disponible</p></div>;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <X className="h-5 w-5" strokeWidth={3} />;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== onClose) {
    t18 = <div className="flex items-center gap-3 bg-blue-600 px-4 py-3 text-white max-md:pt-[max(0.75rem,env(safe-area-inset-top))]">{t14}{t16}<button type="button" onClick={onClose} aria-label="Fermer la conversation" className="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white">{t17}</button></div>;
    $[23] = onClose;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="flex-1" aria-hidden="true" />;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== messages) {
    t20 = messages.map(_temp2);
    $[26] = messages;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== isTyping) {
    t21 = isTyping && <div className="flex justify-start"><div className="rounded-2xl rounded-bl-sm border border-gray-200 bg-white px-4 py-3 shadow-sm"><span className="flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" /></span></div></div>;
    $[28] = isTyping;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div ref={messagesEndRef} />;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== t20 || $[32] !== t21) {
    t23 = <div className="flex flex-1 flex-col space-y-3 overflow-y-auto bg-gray-50 p-4">{t19}{t20}{t21}{t22}</div>;
    $[31] = t20;
    $[32] = t21;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = event_0 => setInput(event_0.target.value);
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== handleKeyDown || $[36] !== input) {
    t25 = <textarea ref={textareaRef} value={input} onChange={t24} onKeyDown={handleKeyDown} rows={1} placeholder="Posez votre question..." className="field-sizing-content max-h-28 flex-1 resize-none bg-transparent text-sm leading-5 text-gray-800 placeholder:text-gray-400 focus:outline-none" />;
    $[35] = handleKeyDown;
    $[36] = input;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== input || $[39] !== isTyping) {
    t26 = !input.trim() || isTyping;
    $[38] = input;
    $[39] = isTyping;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Send className="h-4 w-4" />;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== sendMessage || $[43] !== t26) {
    t28 = <button type="button" onClick={sendMessage} disabled={t26} aria-label="Envoyer le message" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">{t27}</button>;
    $[42] = sendMessage;
    $[43] = t26;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t25 || $[46] !== t28) {
    t29 = <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">{t25}{t28}</div>;
    $[45] = t25;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <p className="mt-1.5 text-center text-[11px] text-gray-400">L'assistant IA peut commettre des erreurs. Vérifiez les informations importantes.</p>;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t29) {
    t31 = <div className="border-t border-gray-200 bg-white p-3 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]">{t29}{t30}</div>;
    $[49] = t29;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== mobileStyle || $[52] !== t12 || $[53] !== t13 || $[54] !== t18 || $[55] !== t23 || $[56] !== t31) {
    t32 = <div role="dialog" aria-label="Conversation avec l'assistant IA" aria-hidden={t12} className={t13} style={mobileStyle}>{t18}{t23}{t31}</div>;
    $[51] = mobileStyle;
    $[52] = t12;
    $[53] = t13;
    $[54] = t18;
    $[55] = t23;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  return t32;
}
function _temp2(message) {
  return <div key={message.id} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap wrap-anywhere ${message.role === "user" ? "rounded-br-sm bg-blue-600 text-white" : "rounded-bl-sm border border-gray-200 bg-white text-gray-800 shadow-sm"}`}>{message.content}</div></div>;
}
function _temp(prev_0) {
  return [...prev_0, {
    id: Date.now(),
    role: "assistant",
    content: "Merci pour votre message ! L'assistant IA est en cours de d\xE9veloppement et sera bient\xF4t disponible."
  }];
}